import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

function Gallery4({ items = [] }) {
  const [carouselApi, setCarouselApi] = useState();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    updateSelection();
    carouselApi.on("select", updateSelection);
    carouselApi.on("reInit", updateSelection);

    return () => {
      carouselApi.off("select", updateSelection);
      carouselApi.off("reInit", updateSelection);
    };
  }, [carouselApi]);

  if (!items.length) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="mb-5 flex items-center justify-end gap-2 md:mb-6">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Previous case study"
          onClick={() => carouselApi?.scrollPrev()}
          disabled={!canScrollPrev}
          className="h-10 w-10 rounded-full border border-[--border-hover] bg-[--bg-secondary]/80 text-[--text-primary] hover:bg-[--bg-tertiary] disabled:pointer-events-none disabled:opacity-40"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Next case study"
          onClick={() => carouselApi?.scrollNext()}
          disabled={!canScrollNext}
          className="h-10 w-10 rounded-full border border-[--border-hover] bg-[--bg-secondary]/80 text-[--text-primary] hover:bg-[--bg-tertiary] disabled:pointer-events-none disabled:opacity-40"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <Carousel
        setApi={setCarouselApi}
        opts={{
          align: "start",
          containScroll: "trimSnaps",
          breakpoints: {
            "(max-width: 768px)": {
              dragFree: true,
            },
          },
        }}
      >
        <CarouselContent className="ml-0">
          {items.map((item) => (
            <CarouselItem
              key={item.id}
              className="basis-[90%] pl-0 pr-4 sm:basis-[72%] md:pr-5 lg:basis-[46%] xl:basis-[38%]"
            >
              <a href={item.href} className="group block h-full rounded-2xl">
                <article className="case-carousel-card">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    loading="lazy"
                  />
                  <div className="case-carousel-overlay absolute inset-0" />

                  <div className="relative z-10 flex h-full flex-col justify-between p-6 md:p-7">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-white/20 bg-black/35 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.12em] text-white/80">
                        {item.category}
                      </span>
                      {item.isNew && (
                        <span className="rounded-full border border-[--accent]/60 bg-[--accent] px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.12em] text-[--bg-primary]">
                          New
                        </span>
                      )}
                    </div>

                    <div className="rounded-xl border border-white/15 bg-black/40 p-4 backdrop-blur-[1.8px]">
                      <p className="text-[11px] font-mono uppercase tracking-[0.13em] text-white/75">
                        {item.client}
                      </p>
                      <h3 className="mt-2 text-lg font-semibold leading-tight text-white md:text-xl">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/85">
                        {item.description}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.15em] text-[--accent] transition-all group-hover:gap-3">
                        Learn more
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </article>
              </a>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="mt-6 flex justify-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`Go to case study ${index + 1}`}
            onClick={() => carouselApi?.scrollTo(index)}
            className={`h-2 w-8 rounded-full transition-colors ${
              currentSlide === index
                ? "bg-[--accent]"
                : "bg-[--border-hover] hover:bg-[--text-muted]"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export { Gallery4 };
